import { Fragment, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, DialogProps, RadioValue, SwitchValue } from 'tdesign-react';

import NovaDialog from '@/components/dialog';
import { UserResourceType } from '@/interface/library';
import { RichMediaValue } from '@/interface/media';
import { INewUploadIProps } from '@/interface/upload';
import { ILibraryDialogRef } from '@/pages/library/dialog';

import { emitRichMediaFile, getRichLocalPath } from '../../change-data';
import UploadDialogContent from '../common/content/dialog';

export interface IUploadDialogDialog extends INewUploadIProps {
  confirmCallback?: () => void;
  closeCallback?: () => void;
  dialogRef?: React.ForwardedRef<ILibraryDialogRef>;
  type?: UserResourceType;
  dialogConfig?: DialogProps;
  handleDialogOnchange: (result: RichMediaValue) => void;
}

/**
 * 上传弹窗
 * 包含上传视频，上传音频
 */
function UploadDialog(props: IUploadDialogDialog) {
  const {
    disabled = false,
    dialogRef,
    closeCallback,
    dialogConfig,
    confirmCallback,
    value,
    handleDialogOnchange,
    hasSetting = false,
  } = props;

  const [visible, setVisible] = useState(false);
  const [info, setInfo] = useState(value);
  const infoRef = useRef(info);

  useImperativeHandle(dialogRef, () => ({
    close,
    open,
  }));

  useEffect(() => {
    setInfo(value);
  }, [value, visible]);

  useEffect(() => {
    infoRef.current = info;
  }, [info]);

  const close = () => {
    closeCallback?.();
    setVisible(false);
  };

  const open = () => {
    setVisible(true);
  };

  const handleDelete = () => {
    const result = {
      ...info,
      url: {
        ...info?.url,
        url: '',
        localPath: '',
        name: '',
      },
    };
    setInfo(result);
  };

  const handleChange = (params: { url: string; name: string }) => {
    const { url, name } = params;
    emitRichMediaFile(url);
    const localPath = getRichLocalPath(url);
    const result = {
      ...info,
      url: {
        ...info?.url,
        url,
        localPath,
        name: name || localPath,
      },
    };
    setInfo(result);
  };

  const confirm = () => {
    handleDialogOnchange(infoRef.current as RichMediaValue);
    confirmCallback?.();
    close();
  };

  const handleRichMediaChange = (
    key: string,
    value: RadioValue | SwitchValue,
  ) => {
    const result = {
      ...info,
      [key]: value,
    };
    setInfo(result);
  };

  return (
    <Fragment>
      <NovaDialog
        dialogConfig={{
          visible,
          closeBtn: false,
          confirmBtn: (
            <Button theme='primary' disabled={disabled} onClick={confirm}>
              确定
            </Button>
          ),
          cancelBtn: (
            <Button theme='default' onClick={close}>
              取消
            </Button>
          ),
          style: {
            width: '420px',
          },
          ...dialogConfig,
        }}
      >
        <UploadDialogContent
          {...props}
          handleRichMediaChange={handleRichMediaChange}
          close={close}
          open={open}
          visible={visible}
          handleChange={handleChange}
          handleDelete={handleDelete}
          value={info}
          hasSetting={hasSetting}
        />
      </NovaDialog>
    </Fragment>
  );
}

export default UploadDialog;
